import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useMutation } from '@tanstack/react-query';
import { useToast } from '@cad-arc';
import { compact } from 'lodash';
import { FieldNameEnum } from '@mark43/cad-api';
import { RmsIntegrationResource } from '@mark43/cad-api';
import { useCallback } from 'react';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';
import { FormInputButton } from '../../forms/integrated-form-components/form-button';
import { useOpenDepartmentLink } from '../../layout/use-open-department-link';
import { useEventMetadataStore } from './event-metadata';

export const GenerateTowReportButton = ({
    agencyEventId,
    towVehicleId,
}: {
    agencyEventId: number;
    towVehicleId: number;
}) => {
    const apiClient = useApiClient();

    const generateTowReportMutation = useMutation(
        ({ agencyEventId, towVehicleId }: { agencyEventId: number; towVehicleId: number }) => {
            return apiClient.request<RmsIntegrationResource.GetOrCreateTowVehicleReport>({
                method: 'POST',
                url: `rms_integration/${agencyEventId}/tow_vehicle/${towVehicleId}/`,
            });
        }
    );
    const openDepartmentLink = useOpenDepartmentLink();

    const toast = useToast();

    const eventMetadata = useEventMetadataStore().state.metadata;
    const eventAgencyHasRen = !!(eventMetadata?.agencyId &&
    eventMetadata?.agencyIdToReportingEventNumbers
        ? eventMetadata.agencyIdToReportingEventNumbers[eventMetadata.agencyId] || []
        : []
    ).length;

    const generateTowReport = useCallback(() => {
        return generateTowReportMutation
            .mutateAsync({ agencyEventId, towVehicleId })
            .then((resp) => {
                const reportId = resp.data.data?.reportId;
                if (reportId) {
                    openDepartmentLink({
                        path: `/reports/${reportId}/`,
                        app: 'RMS',
                    });
                }
            })
            .catch(
                createApiErrorHandler(() => {
                    toast({
                        status: 'error',
                        description: errorOccurredWhile('generating tow report'),
                    });
                })
            );
    }, [generateTowReportMutation, agencyEventId, towVehicleId, openDepartmentLink, toast]);

    const { fieldDisplayNameByFieldName: fieldDisplayNames } = useBootstrapState();

    const renDisplayName =
        fieldDisplayNames[FieldNameEnum.AGENCY_EVENT_REPORTING_EVENT_NUMBER.name];

    const buttonText = compact([
        'NCIC Report',
        !eventAgencyHasRen ? `${renDisplayName} Required` : '',
    ]).join(': ');

    // Not a form input, but it should be disabled when the form is not edtiable (i.e. cleared)
    return (
        <FormInputButton
            disabled={!eventAgencyHasRen}
            size="sm"
            isLoading={generateTowReportMutation.isLoading}
            variant="ghost"
            onClick={generateTowReport}
            trailingVisual="OpenNewWindow"
        >
            {buttonText}
        </FormInputButton>
    );
};
